// apps/dashboard/src/lib/redirect-url.ts

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || '';
const LANDING_URL = process.env.NEXT_PUBLIC_LANDING_URL || '';

const TRUSTED_ORIGINS = [DASHBOARD_URL, LANDING_URL]
  .filter(Boolean)
  .map((u) => {
    try {
      return new URL(u).origin;
    } catch {
      return '';
    }
  })
  .filter(Boolean);

/**
 * Validates a `?redirect=` query value and resolves it to a safe navigation target.
 * Shared by both auth pages (login/register) so a post-auth `?redirect=` doesn't need a
 * second, divergent implementation — see the FIX 1 review note that flagged the register
 * page for dropping this param entirely.
 *
 * `useSearchParams().get()` already URL-decodes once. A previous version of this helper
 * (login page only) called `decodeURIComponent` again on top of that — corrupting any
 * value containing a literal `%` (e.g. an already-encoded nested path), and throwing
 * URIError on a malformed sequence, crashing the whole page. Do not decode again here.
 *
 * Accepts only:
 *   - a same-origin relative path (leading `/`, not `//` — that's protocol-relative and
 *     would navigate off-origin)
 *   - an absolute URL whose origin is one of our own apps (TRUSTED_ORIGINS)
 * Anything else — a foreign https://, a bare "evil.com", a backslash/tab/newline variant
 * some browsers/URL parsers normalize into an effective scheme or authority change, or
 * unparseable garbage — falls back to `/`.
 */
export function getRedirectUrl(redirectParam: string | null | undefined): string {
  if (!redirectParam) return '/';

  // Backslashes are treated as path/authority separators by some URL parsers (WHATWG
  // URL treats `\` like `/` for special schemes), and raw tab/newline can be used to
  // sneak a scheme past a naive startsWith check. Reject outright rather than trying to
  // normalize them.
  if (/[\\\t\n\r]/.test(redirectParam)) return '/';

  if (redirectParam.startsWith('/') && !redirectParam.startsWith('//')) {
    return redirectParam;
  }

  try {
    const url = new URL(redirectParam);
    if (TRUSTED_ORIGINS.includes(url.origin)) return redirectParam;
  } catch {
    // Not a parseable absolute URL — fall through to the safe default.
  }

  return '/';
}
